import React, { useState } from "react";
import { Dialog, IconButton } from "@mui/material";
import { X, AlertTriangle, AlertCircle } from "lucide-react";
import { ExplanationRequest } from "../../../types/explanations";
import { getSchoolClassLabel } from "../../common/utils";

interface RejectRequestModalProps {
  open: boolean;
  request: ExplanationRequest | any;
  onReject: (rejectionReason: string) => void;
  onClose: () => void;
}

const RejectRequestModal: React.FC<RejectRequestModalProps> = ({
  open,
  request,
  onReject,
  onClose,
}) => {
  const [rejectionReason, setRejectionReason] = useState<string>("");

  const handleReject = () => {
    if (rejectionReason.trim()) {
      onReject(rejectionReason.trim());
    }
  };

  const isFormValid = rejectionReason.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 9999,
        "& .MuiDialog-paper": {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header moderne */}
      <div className="bg-red-600 text-white">
        <div className="p-4 sm:p-6 lg:p-8 flex justify-between items-center">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold">
                Rejeter la demande
              </h2>
              <p className="text-sm sm:text-base text-white/90 mt-1">
                Cette action est irréversible
              </p>
            </div>
          </div>
          <IconButton
            onClick={onClose}
            className="text-white hover:bg-white/20 transition-colors rounded-xl"
            sx={{
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
            }}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </IconButton>
        </div>
      </div>

      {/* Contenu moderne */}
      <div className="bg-gray-50 min-h-[400px] max-h-[70vh] overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="space-y-6">
            {/* Avertissement */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-red-900">Attention</h3>
                  <p className="text-sm text-red-800 mt-1">
                    Cette action enverra un email de rejet à l'élève et ne
                    pourra pas être annulée.
                  </p>
                </div>
              </div>
            </div>

            {/* Informations de la demande */}
            <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">
                    Élève
                  </p>
                  <p className="text-sm text-gray-800">
                    {request.student
                      ? `${request.student.firstname} ${request.student.lastname}`
                      : "Non disponible"}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">
                    Classe
                  </p>
                  <p className="text-sm text-gray-800">
                    {getSchoolClassLabel(request.schoolClass)}
                  </p>
                </div>
              </div>
            </div>

            {/* Raison de rejet */}
            <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Raison du rejet
                </h3>
              </div>

              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Veuillez expliquer la raison du rejet de cette demande..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={4}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-2">
                {rejectionReason.length}/500 caractères
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer moderne */}
      <div className="bg-gray-100 border-t border-gray-200 p-4 sm:p-6">
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium text-sm sm:text-base"
          >
            Annuler
          </button>
          <button
            onClick={handleReject}
            disabled={!isFormValid}
            className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium text-sm sm:text-base"
          >
            Rejeter la demande
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default RejectRequestModal;
